export type PlacementRect = { x: number; y: number; width: number; height: number };
export type PlacementSize = { width: number; height: number };
export type PlacementSide = 'TOP' | 'BOTTOM' | 'LEFT' | 'RIGHT';

export const TOOLTIP_GAP = 12;

const OPPOSITE_SIDE: Record<PlacementSide, PlacementSide> = {
  TOP: 'BOTTOM',
  BOTTOM: 'TOP',
  LEFT: 'RIGHT',
  RIGHT: 'LEFT'
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), Math.max(min, max));

/**
 * The authored `positionType` also carries an alignment suffix (`TOP_LEFT`,
 * `LEFT_BOTTOM`, ...) that only tooltips use. A hotspot is always centred on
 * its anchor, so only the leading side matters here.
 */
export const toPlacementSide = (positionType?: string | null): PlacementSide => {
  const side = (positionType ?? '').split('_')[0];
  return side === 'TOP' || side === 'LEFT' || side === 'RIGHT' ? side : 'BOTTOM';
};

/**
 * Places the tooltip next to its beacon, in host viewport coordinates.
 *
 * The player runs inside an iframe that is sized to the tooltip itself, so it
 * cannot read the host viewport from its own `window` — passing it in is what
 * keeps the tooltip anchored to the beacon instead of collapsing into the
 * top-left corner.
 */
export const resolveTooltipPlacement = ({
  anchor,
  tooltip,
  viewport,
  positionType,
  gap = TOOLTIP_GAP
}: {
  anchor: PlacementRect;
  tooltip: PlacementSize;
  viewport?: PlacementSize | null;
  positionType?: string | null;
  gap?: number;
}): { top: number; left: number; side: PlacementSide } => {
  const preferred = toPlacementSide(positionType);
  const hasViewport = Boolean(viewport && viewport.width > 0 && viewport.height > 0);

  const fits: Record<PlacementSide, boolean> = {
    TOP: !hasViewport || anchor.y - gap - tooltip.height >= 0,
    BOTTOM: !hasViewport || anchor.y + anchor.height + gap + tooltip.height <= viewport!.height,
    LEFT: !hasViewport || anchor.x - gap - tooltip.width >= 0,
    RIGHT: !hasViewport || anchor.x + anchor.width + gap + tooltip.width <= viewport!.width
  };

  const side = fits[preferred]
    ? preferred
    : fits[OPPOSITE_SIDE[preferred]]
    ? OPPOSITE_SIDE[preferred]
    : preferred;

  const centeredLeft = anchor.x + anchor.width / 2 - tooltip.width / 2;
  const centeredTop = anchor.y + anchor.height / 2 - tooltip.height / 2;

  let top: number;
  let left: number;

  switch (side) {
    case 'TOP':
      top = anchor.y - gap - tooltip.height;
      left = centeredLeft;
      break;
    case 'LEFT':
      top = centeredTop;
      left = anchor.x - gap - tooltip.width;
      break;
    case 'RIGHT':
      top = centeredTop;
      left = anchor.x + anchor.width + gap;
      break;
    case 'BOTTOM':
    default:
      top = anchor.y + anchor.height + gap;
      left = centeredLeft;
      break;
  }

  if (!hasViewport) return { top, left, side };

  return {
    top: clamp(top, gap, viewport!.height - tooltip.height - gap),
    left: clamp(left, gap, viewport!.width - tooltip.width - gap),
    side
  };
};
